import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Shield, Lock, Eye, Database, Users, Mail, Globe } from 'lucide-react';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "January 28, 2026";

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back to MyHairChair</span>
          </Link>
          <Shield className="w-6 h-6 text-amber-400" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-zinc-400">Last Updated: {lastUpdated}</p>
        </div>

        {/* Quick Summary */}
        <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30 mb-8">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-amber-400 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Quick Summary
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                <span className="text-zinc-300">Your data is encrypted and secure</span>
              </div>
              <div className="flex items-start gap-2">
                <Database className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                <span className="text-zinc-300">We only collect necessary information</span>
              </div>
              <div className="flex items-start gap-2">
                <Users className="w-4 h-4 text-purple-400 mt-1 flex-shrink-0" />
                <span className="text-zinc-300">We never sell your personal data</span>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-amber-400 mt-1 flex-shrink-0" />
                <span className="text-zinc-300">You can request data deletion anytime</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="space-y-8">
          <Section
            title="1. Information We Collect"
            icon={<Database className="w-5 h-5 text-blue-400" />}
          >
            <p className="mb-4">We collect information you provide directly to us, including:</p>
            <ul className="list-disc pl-6 space-y-2 text-zinc-300">
              <li><strong>Account Information:</strong> Name, email address, phone number, and password when you create an account.</li>
              <li><strong>Profile Information:</strong> Business name, location, services offered, portfolio images, and availability.</li>
              <li><strong>Payment Information:</strong> Payment methods (processed securely through Stripe), transaction history, and billing address.</li>
              <li><strong>Booking Information:</strong> Appointment details, client communications, and service preferences.</li>
              <li><strong>Device Information:</strong> IP address, browser type, device type, and operating system for security and analytics.</li>
            </ul>
          </Section>

          <Section
            title="2. How We Use Your Information"
            icon={<Globe className="w-5 h-5 text-green-400" />}
          >
            <p className="mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2 text-zinc-300">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send appointment reminders, confirmations, and notifications</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
              <li>Personalize and improve your experience</li>
              <li>Send promotional communications (with your consent)</li>
            </ul>
          </Section>

          <Section
            title="3. Information Sharing"
            icon={<Users className="w-5 h-5 text-purple-400" />}
          >
            <p className="mb-4">We may share your information in the following situations:</p>
            <ul className="list-disc pl-6 space-y-2 text-zinc-300">
              <li><strong>With Service Providers:</strong> We share information with third-party vendors who perform services on our behalf (e.g., payment processing via Stripe, email delivery via SendGrid).</li>
              <li><strong>With Clients/Stylists:</strong> When you book an appointment, relevant information is shared between the client and stylist.</li>
              <li><strong>For Legal Reasons:</strong> We may disclose information if required by law or to protect our rights, privacy, safety, or property.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred.</li>
            </ul>
            <p className="mt-4 text-amber-400 font-semibold">We never sell your personal information to third parties.</p>
          </Section>

          <Section
            title="4. Data Security"
            icon={<Lock className="w-5 h-5 text-amber-400" />}
          >
            <p className="mb-4">We take reasonable measures to help protect your personal information from loss, theft, misuse, and unauthorized access, including:</p>
            <ul className="list-disc pl-6 space-y-2 text-zinc-300">
              <li>Encryption of data in transit (SSL/TLS) and at rest</li>
              <li>Secure password hashing using industry-standard algorithms</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication mechanisms</li>
              <li>PCI-DSS compliant payment processing through Stripe</li>
            </ul>
          </Section>

          <Section
            title="5. Your Rights and Choices"
            icon={<Shield className="w-5 h-5 text-green-400" />}
          >
            <p className="mb-4">You have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-2 text-zinc-300">
              <li><strong>Access:</strong> You can request a copy of your personal data at any time.</li>
              <li><strong>Correction:</strong> You can update or correct your information through your account settings.</li>
              <li><strong>Deletion:</strong> You can request deletion of your account and associated data by contacting us.</li>
              <li><strong>Opt-out:</strong> You can opt out of promotional emails by clicking the unsubscribe link.</li>
              <li><strong>Data Portability:</strong> You can request your data in a portable format.</li>
            </ul>
            <p className="mt-4 text-zinc-400">To exercise these rights, contact us at <a href="mailto:privacy@myhairchair.com" className="text-amber-400 hover:underline">privacy@myhairchair.com</a>.</p>
          </Section>

          <Section
            title="6. Cookies and Tracking"
            icon={<Eye className="w-5 h-5 text-blue-400" />}
          >
            <p className="mb-4">We use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 space-y-2 text-zinc-300">
              <li>Keep you logged in to your account</li>
              <li>Remember your preferences and settings</li>
              <li>Analyze how our services are used</li>
              <li>Improve our services and user experience</li>
            </ul>
            <p className="mt-4 text-zinc-400">You can control cookies through your browser settings, though some features may not function properly if cookies are disabled.</p>
          </Section>

          <Section
            title="7. Children's Privacy"
            icon={<Users className="w-5 h-5 text-red-400" />}
          >
            <p className="text-zinc-300">
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately at <a href="mailto:privacy@myhairchair.com" className="text-amber-400 hover:underline">privacy@myhairchair.com</a>.
            </p>
          </Section>

          <Section
            title="8. Changes to This Policy"
            icon={<Mail className="w-5 h-5 text-amber-400" />}
          >
            <p className="text-zinc-300">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.
            </p>
          </Section>

          <Section
            title="9. Contact Us"
            icon={<Mail className="w-5 h-5 text-green-400" />}
          >
            <p className="text-zinc-300 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-zinc-900/50 rounded-lg p-4 space-y-2 text-zinc-300">
              <p><strong>Email:</strong> <a href="mailto:privacy@myhairchair.com" className="text-amber-400 hover:underline">privacy@myhairchair.com</a></p>
              <p><strong>Website:</strong> <a href="https://myhairchair.com" className="text-amber-400 hover:underline">myhairchair.com</a></p>
              <p><strong>Address:</strong> MyHairChair LLC, United States</p>
            </div>
          </Section>
        </div>

        {/* Footer CTA */}
        <div className="mt-12 text-center">
          <Link to="/">
            <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to MyHairChair
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-zinc-500 text-sm">
          <p>&copy; {new Date().getFullYear()} MyHairChair. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-amber-400">Terms of Service</Link>
            <span>•</span>
            <Link to="/privacy-policy" className="text-amber-400">Privacy Policy</Link>
            <span>•</span>
            <Link to="/contact" className="hover:text-amber-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-white flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-zinc-300">
        {children}
      </CardContent>
    </Card>
  );
}
